import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { HomeLayout } from "../components/layout/HomeLayout";
import { HubEditorialGrid, type HubEditorialItem } from "../components/home/HubEditorialGrid";
import { HOME_THEME as C } from "../lib/homeTheme";

/** Rutas guiadas del hub Aprender. */
const LEARN_PATH_ITEMS: HubEditorialItem[] = [
  {
    id: "path",
    title: "Tu camino",
    description: "De cero a tu primera melodía en cuatro pasos ordenados.",
    path: "/learn/path",
    accent: "mint",
  },
  {
    id: "guitar",
    title: "Conociendo la guitarra",
    description: "Técnica, digitación y ejercicios progresivos.",
    path: "/fingers",
    accent: "mint",
  },
  {
    id: "scales",
    title: "Escalas",
    description: "Misiones con micrófono, recorridos y sistema CAGED.",
    path: "/scales",
    accent: "lavender",
  },
  {
    id: "harmony",
    title: "Armonía",
    description: "Acordes, voicings y progresiones con misiones.",
    path: "/chords",
    accent: "mint",
  },
];

/**
 * Módulos libres (sandbox) — mismos destinos que Explorar.
 * Quedan en Aprender para uso futuro; no forman parte del mapa de caminos.
 */
const LEARN_FREE_MODULES: HubEditorialItem[] = [
  {
    id: "scales",
    title: "Escalas",
    description: "Explorador libre de escalas en el mástil.",
    path: "/scales/explorer",
    accent: "lavender",
  },
  {
    id: "chords",
    title: "Acordes",
    description: "Constructor de acordes y voicings.",
    path: "/chords/explorer",
    accent: "mint",
  },
  {
    id: "modos",
    title: "Modos",
    description: "Modos griegos y sonoridades.",
    path: "/modos",
    accent: "lavender",
  },
  {
    id: "notas",
    title: "Notas",
    description: "Ubica notas en el instrumento.",
    path: "/notas",
    accent: "mint",
  },
  {
    id: "melodies",
    title: "Melodías",
    description: "Practica frases melódicas.",
    path: "/melodies",
    accent: "lavender",
  },
];

export const LearnHubPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <HomeLayout>
      <div className="mx-auto max-w-7xl px-8 py-10 pb-24">
        <button
          type="button"
          onClick={() => navigate("/home")}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#c4b5fd]"
          style={{ color: C.muted }}
        >
          <ArrowLeft className="h-4 w-4" />
          Inicio
        </button>

        <header className="mt-10 mb-16 max-w-3xl text-left">
          <p
            className="text-xs tracking-widest uppercase"
            style={{ color: "rgba(236, 72, 153, 0.55)" }}
          >
            Aprender
          </p>
          <h1
            className="mt-4 font-serif font-light tracking-tight text-left text-[clamp(3rem,8vw,4.5rem)] leading-[0.95]"
            style={{ color: C.text }}
          >
            Rutas guiadas
          </h1>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-left text-white/60">
            Técnica instrumental, escalas y armonía organizadas en misiones pedagógicas.
          </p>
        </header>

        <HubEditorialGrid items={LEARN_PATH_ITEMS} />

        <section className="mt-20 pt-12 border-t border-white/[0.08]" aria-labelledby="learn-free-heading">
          <div className="mb-10 max-w-2xl">
            <p
              className="text-xs tracking-widest uppercase"
              style={{ color: "rgba(196, 181, 253, 0.5)" }}
            >
              Módulos libres
            </p>
            <h2
              id="learn-free-heading"
              className="mt-3 font-serif font-light text-[clamp(1.75rem,3.5vw,2.5rem)] tracking-tight"
              style={{ color: C.text }}
            >
              Escalas, acordes y más
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/55">
              Sandbox sin misiones. Los usaremos más adelante en los caminos; por ahora están
              disponibles aquí.
            </p>
          </div>
          <HubEditorialGrid items={LEARN_FREE_MODULES} columns={3} />
        </section>
      </div>
    </HomeLayout>
  );
};

export default LearnHubPage;
